import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Calendar, ArrowLeft, Tag, Send, CheckCircle, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLang } from '../i18n/LanguageContext';
import ShareButtons from '../components/ShareButtons';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { categoryLabel, formatBlogDate } from '../lib/blogCategories';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogArticle = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, lang } = useLang();
  const articleLang = searchParams.get('lang') || lang;

  useEffect(() => {
    const loadPost = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${BACKEND_URL}/api/public/blog/posts/${slug}?lang=${articleLang}`);
        setPost(response.data);
        const allPosts = await axios.get(`${BACKEND_URL}/api/public/blog/posts?category=${response.data.category}&lang=${articleLang}`);
        setRelatedPosts(allPosts.data.filter(p => p.slug !== slug).slice(0, 2));
      } catch (error) { setLoading(false); }
      finally { setLoading(false); }
    };
    loadPost();
  }, [slug, articleLang]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black pt-24 flex items-center justify-center">
        <p className="text-gray-400 text-lg">{t.blog.loading}</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-black pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 text-center py-20">
          <h2 className="text-3xl font-bold text-white mb-4">{t.blog.notFound}</h2>
          <Link to="/blog">
            <Button to="/blog" className="bg-purple-600 hover:bg-purple-700">{t.blog.backToBlog}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.metaDescription || post.excerpt,
    "image": post.image,
    "datePublished": post.createdAt,
    "dateModified": post.updatedAt,
    "author": {
      "@type": "Organization",
      "name": "Party LED",
      "url": "https://www.party-led.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Party LED",
      "url": "https://www.party-led.com"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.party-led.com/blog/${post.slug}`
    }
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <>
        <title>{post.metaTitle || `${post.title} | Party LED`}</title>
        <meta name="description" content={post.metaDescription || post.excerpt} />
        <link rel="canonical" href={`https://www.party-led.com/blog/${post.slug}`} />
        <meta property="og:title" content={post.metaTitle || post.title} />
        <meta property="og:description" content={post.metaDescription || post.excerpt} />
        <meta property="og:image" content={post.image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Party LED" />
        <meta property="og:url" content={`https://www.party-led.com/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.metaTitle || post.title} />
        <meta name="twitter:description" content={post.metaDescription || post.excerpt} />
        <meta name="twitter:image" content={post.image} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back button */}
        <Link to="/blog" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8" data-testid="blog-back-link">
          <ArrowLeft className="w-4 h-4" />
          {t.blog.backToBlog}
        </Link>

        {/* Hero Image */}
        {post.image && (
          <div className="rounded-xl overflow-hidden mb-8 aspect-video" data-testid="article-hero-image">
            <img loading="lazy"
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Article Header */}
        <header className="mb-8" data-testid="article-header">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-purple-600/90 text-white text-xs font-medium px-3 py-1 rounded-full">
              {categoryLabel(post.category, lang)}
            </span>
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Calendar className="w-3.5 h-3.5" />
              <time dateTime={post.createdAt}>
                {formatBlogDate(post.createdAt, lang)}
              </time>
            </div>
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Georgia, serif' }} data-testid="article-title">
            {post.title}
          </h1>

          <p className="text-gray-400 text-lg leading-relaxed">
            {post.excerpt}
          </p>
        </header>

        {/* Article Content */}
        <article
          className="prose prose-invert prose-lg max-w-none 
            prose-headings:font-bold prose-headings:text-white prose-headings:font-serif
            prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
            prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
            prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-4
            prose-li:text-gray-300
            prose-strong:text-white
            prose-em:text-purple-300
            prose-a:text-purple-400 prose-a:hover:text-purple-300
            prose-ul:my-4 prose-ol:my-4"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
          data-testid="article-content"
        />

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-gray-800" data-testid="article-tags">
            {post.tags.map((tag) => (
              <span key={tag} className="flex items-center gap-1.5 text-sm text-gray-400 bg-gray-800 px-3 py-1.5 rounded-lg">
                <Tag className="w-3.5 h-3.5" />
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Share + CTA */}
        <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4">
          <ShareButtons path={`/blog/${post.slug}`} title={post.title} />
          <Link to="/contact" className="sm:ml-auto">
            <Button className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto" data-testid="article-cta">
              {t.blog.cta}
            </Button>
          </Link>
        </div>

        {/* Newsletter CTA */}
        <NewsletterBlock />

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="mt-16 pt-10 border-t border-gray-800" data-testid="related-posts">
            <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Georgia, serif' }}>
              {t.blog.related}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedPosts.map((related) => (
                <Link
                  key={related.id}
                  to={`/blog/${related.slug}`}
                  className="group bg-gray-900 rounded-xl overflow-hidden border border-gray-800 hover:border-purple-600/50 transition-all"
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={related.image}
                      alt={related.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="text-white font-bold group-hover:text-purple-400 transition-colors line-clamp-2">
                      {related.title}
                    </h3>
                    <p className="text-gray-400 text-sm mt-2 line-clamp-2">{related.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const NewsletterBlock = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useLang();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/newsletter/subscribe`, { email });
      setSubscribed(true);
      setEmail('');
    } catch (error) {
      setSubscribed(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-12 bg-gradient-to-br from-purple-900/30 to-pink-900/20 border border-purple-700/30 rounded-2xl p-8 md:p-10" data-testid="newsletter-block">
      <div className="flex flex-col md:flex-row items-center gap-6">
        <div className="flex-shrink-0">
          <div className="w-14 h-14 rounded-xl bg-purple-600/20 flex items-center justify-center">
            <Mail className="w-7 h-7 text-purple-400" />
          </div>
        </div>
        <div className="flex-1 text-center md:text-left">
          <h3 className="text-xl font-bold text-white mb-1">{t.blog.newsletter}</h3>
          <p className="text-gray-400 text-sm">{t.blog.newsletterDesc}</p>
        </div>
        <div className="w-full md:w-auto">
          {subscribed ? (
            <div className="flex items-center gap-2 text-green-400 font-medium" data-testid="blog-newsletter-success">
              <CheckCircle className="w-5 h-5" />
              <span>{t.blog.subscribed}</span>
            </div>
          ) : (
            <form onSubmit={handleSubscribe} className="flex gap-2">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="votre@email.com" required className="bg-black/40 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-purple-500/50 w-56" data-testid="blog-newsletter-input" />
              <Button type="submit" disabled={loading} className="bg-purple-600 hover:bg-purple-500" data-testid="blog-newsletter-submit">
                <Send className="w-4 h-4 mr-2" />
                {loading ? '...' : t.blog.subscribe}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogArticle;
